import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Button,
  useColorModeValue,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Divider,
  List,
  ListItem,
  ListIcon,
  Icon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
} from '@chakra-ui/react';
import {
  FaPlay,
  FaExternalLinkAlt,
  FaCheck,
  FaUsers,
  FaMobile,
  FaDesktop,
} from 'react-icons/fa';
import { demoConfig } from '../config/demoConfig';
import { launchPokerWithAutoLogin } from '../services/autoLoginService';
import { useTranslation } from 'react-i18next';

const DemoPage: React.FC = () => {
  const { t } = useTranslation();
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');
  const toast = useToast();
  const [isLaunching, setIsLaunching] = useState(false);

  const handleLaunch = async (clientUrl: string, demoType: string) => {
    setIsLaunching(true);
    try {
      await launchPokerWithAutoLogin(clientUrl);
    } catch (error) {
      toast({
        title: t('heroSection.toast.error'),
        description: t('heroSection.toast.errorDesc'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLaunching(false);
    }
  };

  const demoInstances = [
    {
      title: t('demoPage.cards.desktop.title'),
      description: t('demoPage.cards.desktop.description'),
      type: 'Desktop',
      status: 'Live',
      players: '200+',
      features: [
        t('demoPage.cards.desktop.features.f1'),
        t('demoPage.cards.desktop.features.f2'),
        t('demoPage.cards.desktop.features.f3'),
        t('demoPage.cards.desktop.features.f4'),
      ],
      demoUrl: demoConfig.pokerDesktop,
      icon: FaDesktop,
      badgeColor: 'blue',
    },
    {
      title: t('demoPage.cards.mobile.title'),
      description: t('demoPage.cards.mobile.description'),
      type: 'Mobile',
      status: 'Live',
      players: '180+',
      features: [
        t('demoPage.cards.mobile.features.f1'),
        t('demoPage.cards.mobile.features.f2'),
        t('demoPage.cards.mobile.features.f3'),
        t('demoPage.cards.mobile.features.f4'),
      ],
      demoUrl: demoConfig.pokerMobile,
      icon: FaMobile,
      badgeColor: 'green',
    },
    {
      title: t('demoPage.cards.clubApp.title'),
      description: t('demoPage.cards.clubApp.description'),
      type: 'ClubApp',
      status: 'Live',
      players: '120+',
      features: [
        t('demoPage.cards.clubApp.features.f1'),
        t('demoPage.cards.clubApp.features.f2'),
        t('demoPage.cards.clubApp.features.f3'),
        t('demoPage.cards.clubApp.features.f4'),
      ],
      demoUrl: demoConfig.clubApp,
      icon: FaUsers,
      badgeColor: 'purple',
    },
  ];

  const mobileDemos = [
    {
      title: t('demoPage.mobile.ios.title'),
      description: t('demoPage.mobile.ios.description'),
      platform: 'iOS',
      version: '2.1.0',
      // demoUrl: demoConfig.iosApp,
      icon: FaMobile,
    },
    {
      title: t('demoPage.mobile.android.title'),
      description: t('demoPage.mobile.android.description'),
      platform: 'Android',
      version: '2.1.0',
      // demoUrl: demoConfig.androidApp,
      icon: FaMobile,
    },
    {
      title: t('demoPage.mobile.desktop.title'),
      description: t('demoPage.mobile.desktop.description'),
      platform: 'Desktop',
      version: '3.0.0',
      // demoUrl: demoConfig.desktopClient,
      icon: FaDesktop,
    },
  ];

  return (
    <Box>
      {/* Main Demo Instances */}
      <Box bg={bgColor} py={16}>
        <Container maxW='7xl'>
          <VStack spacing={12}>
            <VStack spacing={4} textAlign='center'>
              <Heading color='primary.500' size='xl'>
                {t('demoPage.instances.title')}
              </Heading>
              <Text fontSize='xl' color='gray.500' maxW='3xl'>
                {t('demoPage.instances.subtitle')}
              </Text>
              <Alert status='info' maxW='2xl'>
                <AlertIcon />
                <Box>
                  <AlertTitle>{t('demoPage.instances.alert.title')}</AlertTitle>
                  <AlertDescription>
                    {t('demoPage.instances.alert.description')}
                  </AlertDescription>
                </Box>
              </Alert>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
              {demoInstances.map((demo, index) => (
                <Card
                  key={index}
                  bg={cardBg}
                  boxShadow='md'
                  display='flex'
                  flexDirection='column'
                  h='full'
                >
                  <CardHeader>
                    <VStack spacing={3} align='start'>
                      <HStack justify='space-between' w='full'>
                        <Icon as={demo.icon} boxSize={6} color='primary.500' />
                        <Badge colorScheme={demo.badgeColor} variant='solid'>
                          {t('demoPage.instances.statusLive')}
                        </Badge>
                      </HStack>
                      <Heading size='md'>{demo.title}</Heading>
                      <Text fontSize='sm' color='gray.500'>
                        {demo.description}
                      </Text>
                      <HStack spacing={4}>
                        <Badge variant='outline' colorScheme='gray'>
                          {demo.type}
                        </Badge>
                        <Text fontSize='sm' color='gray.500'>
                          {demo.players} {t('demoPage.instances.playersOnline')}
                        </Text>
                      </HStack>
                    </VStack>
                  </CardHeader>

                  <CardBody display='flex' flexDirection='column' flex={1}>
                    <List spacing={2} w='full' flex={1} mb={4}>
                      {demo.features.map((feature, idx) => (
                        <ListItem key={idx} fontSize='sm'>
                          <ListIcon as={FaCheck} color='green.500' />
                          {feature}
                        </ListItem>
                      ))}
                    </List>

                    <Divider mb={4} />

                    <HStack spacing={3} w='full'>
                      <Button
                        leftIcon={<FaPlay />}
                        colorScheme='primary'
                        size='sm'
                        flex={1}
                        onClick={() =>
                          demo.type === 'ClubApp'
                            ? window.open(demo.demoUrl, '_blank')
                            : handleLaunch(demo.demoUrl, demo.type)
                        }
                        isLoading={isLaunching && demo.type !== 'ClubApp'}
                        loadingText={t('demoPage.instances.launch.launching')}
                      >
                        {t('demoPage.instances.launch.button')}
                      </Button>
                      <Button
                        leftIcon={<FaExternalLinkAlt />}
                        variant='outline'
                        size='sm'
                        onClick={() => window.open(demo.demoUrl, '_blank')}
                      >
                        {t('demoPage.instances.launch.open')}
                      </Button>
                    </HStack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Mobile & Desktop Demos */}
      <Box bg={bgColor} py={16}>
        <Container maxW='7xl'>
          <VStack spacing={12}>
            <VStack spacing={4} textAlign='center'>
              <Heading color='primary.500' size='xl'>
                {t('demoPage.mobile.title')}
              </Heading>
              <Text fontSize='lg' color='gray.500' maxW='2xl'>
                {t('demoPage.mobile.subtitle')}
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
              {mobileDemos.map((demo, index) => (
                <Card key={index} bg={cardBg} boxShadow='md'>
                  <CardBody>
                    <VStack spacing={4} textAlign='center'>
                      <Icon as={demo.icon} boxSize={12} color='primary.500' />
                      <Heading size='md'>{demo.title}</Heading>
                      <Text fontSize='sm' color='gray.500'>
                        {demo.description}
                      </Text>
                      <HStack spacing={2}>
                        <Badge variant='outline' colorScheme='gray'>
                          {demo.platform}
                        </Badge>
                        <Text fontSize='xs' color='gray.500'>
                          v{demo.version}
                        </Text>
                      </HStack>

                      <Button
                        leftIcon={<FaExternalLinkAlt />}
                        colorScheme='primary'
                        size='sm'
                        w='full'
                        // onClick={() => window.open(demo.demoUrl, '_blank')}
                      >
                        {t('demoPage.mobile.tryDemo')}
                      </Button>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Demo Instructions */}
      <Box bg={bgColor} py={16}>
        <Container maxW='4xl'>
          <Card bg={cardBg} boxShadow='md'>
            <CardHeader>
              <Heading size='lg'>{t('demoPage.instructions.title')}</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={6} align='start'>
                <Box>
                  <Heading size='md' mb={3}>
                    {t('demoPage.instructions.gettingStarted.title')}
                  </Heading>
                  <List spacing={2}>
                    <ListItem>
                      <ListIcon as={FaCheck} color='green.500' />
                      {t('demoPage.instructions.gettingStarted.items.i1')}
                    </ListItem>
                    <ListItem>
                      <ListIcon as={FaCheck} color='green.500' />
                      {t('demoPage.instructions.gettingStarted.items.i2')}
                    </ListItem>
                    <ListItem>
                      <ListIcon as={FaCheck} color='green.500' />
                      {t('demoPage.instructions.gettingStarted.items.i3')}
                    </ListItem>
                    <ListItem>
                      <ListIcon as={FaCheck} color='green.500' />
                      {t('demoPage.instructions.gettingStarted.items.i4')}
                    </ListItem>
                  </List>
                </Box>

                <Divider />

                <Box>
                  <Heading size='md' mb={3}>
                    {t('demoPage.instructions.autoLogin.title')}
                  </Heading>
                  <Text fontSize='sm' color='gray.500' mb={3}>
                    {t('demoPage.instructions.autoLogin.description')}
                  </Text>
                  <Alert status='info' variant='left-accent'>
                    <AlertIcon />
                    <Text fontSize='sm'>
                      {t('demoPage.instructions.autoLogin.hint')}
                    </Text>
                  </Alert>
                </Box>

                <Divider />

                <Box>
                  <Heading size='md' mb={3}>
                    {t('demoPage.instructions.support.title')}
                  </Heading>
                  <Text fontSize='sm' color='gray.500'>
                    {t('demoPage.instructions.support.description')}
                  </Text>
                  <Button
                    colorScheme='primary'
                    size='sm'
                    mt={3}
                    onClick={() =>
                      window.open(demoConfig.contactSales, '_blank')
                    }
                  >
                    {t('demoPage.instructions.support.contactSales')}
                  </Button>
                </Box>
              </VStack>
            </CardBody>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default DemoPage;
